import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import constants from '../constants';
import SearchBar from './search-bar';
import FontPairsPresetsGroup from './font-pairs-presets-group';
import EmptyState from './empty-state';
import ContentBox from './content-box';
import optionIds from '../constants/option-ids';

const {
  DialogHeader,
  Box,
  Stack,
  DialogButtonGroup,
  DialogButton,
  Paragraph,
  Heading,
  DialogButtonGroupSeparator,
  FloatingAction,
} = designSystem;

const { DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH, DEFAULT_DIALOG_MIN_HEIGHT } = constants;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const filterFonts = (fonts, query) => {
  if (!query.trim()) {
    return fonts;
  }

  const normalizedQuery = query.toLowerCase().trim();

  return fonts.filter(({ label }) => {
    const normalizedLabel = label.toLowerCase().trim();

    return normalizedLabel.includes(normalizedQuery);
  });
};

const DesignFontsDialog = ({
  offsetX,
  offsetY,
  captions,
  onSubmit,
  onCancel,
  fonts,
  fontsData,
  customFonts = [],
  activeFontId,
  setActiveFont,
  fontObserver,
  onAddFontPairClick = null,
  onDeleteFontPairClick = null,
  onEditFontPairClick = null,
  isLoading = false,
  onDragEnd,
}) => {
  const topAnchorRef = useRef(null);
  const prevActiveFontIdRef = useRef(activeFontId);
  const [filteredFonts, setFilteredFonts] = useState(fonts);
  const [filteredCustomFonts, setFilteredCustomFonts] = useState(customFonts);

  const handleSearchQueryChange = (query) => {
    if (query.trim()) {
      trackEvent(events.DESIGN_SETTINGS_SEARCH_PERFORMED, {
        sourceId: optionIds.FONTS,
        searchTerm: query,
      });
    }
    topAnchorRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setFilteredFonts(filterFonts(fonts, query));
    setFilteredCustomFonts(filterFonts(customFonts, query));
  };

  const handleSearchQueryClear = () => {
    topAnchorRef.current?.scrollTo({ top: 0 });
    setFilteredFonts(fonts);
    setFilteredCustomFonts(customFonts);
  };

  const handleSearchFocus = () => {
    trackEvent(events.DESIGN_SETTINGS_SEARCH_CLICKED, {
      sourceId: optionIds.FONTS,
    });
  };

  useEffect(() => {
    setFilteredCustomFonts(customFonts);

    if (activeFontId !== prevActiveFontIdRef.current) {
      setFilteredFonts(fonts);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [customFonts.length]);

  useEffect(() => {
    prevActiveFontIdRef.current = activeFontId;
  }, [activeFontId]);

  return (
    <Modal
      draggable
      fullscreen={bowser.mobile}
      width={DEFAULT_DIALOG_WIDTH}
      minWidth={DEFAULT_DIALOG_WIDTH}
      maxWidth={DEFAULT_DIALOG_WIDTH}
      height={DEFAULT_DIALOG_HEIGHT}
      minHeight={DEFAULT_DIALOG_MIN_HEIGHT}
      offsetX={offsetX}
      offsetY={offsetY}
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-dialog-header-controls"
      overlay="none"
      handleCancel={onCancel}
      handleSubmit={onSubmit}
      onOverlayClick={onSubmit}
      onDragEnd={onDragEnd}
      className="ws-design-fonts-dialog"
      resizeIcon="ns-resize"
    >
      <PanelGroup height="100%">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger"
          style={{ flexShrink: '0' }}
        >
          <DialogHeader paddingBottom="spacing-s">
            <Box>
              <Box paddingBottom="spacing-2-xs">
                <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
              </Box>
              <Box>
                <Stack>
                  <Heading type="heading-3">{captions.title}</Heading>
                  <Paragraph>{captions.description}</Paragraph>
                </Stack>
              </Box>
            </Box>
          </DialogHeader>
        </Panel>
        <Panel
          height="100%"
          scrollable
          style={{ borderBottom: 'none' }}
          rootRef={topAnchorRef}
          className="ws-design-fonts-dialog__content-wrapper"
        >
          <div className="ws-design-fonts-dialog__content">
            <SearchBar
              sticky
              placeholder={captions.searchPlaceholder}
              onChange={handleSearchQueryChange}
              onClear={handleSearchQueryClear}
              onFocus={handleSearchFocus}
            />
            <ContentBox>
              {!filteredFonts.length && !filteredCustomFonts.length ? (
                <EmptyState
                  title={captions.emptyStateTitle}
                  subtitle={captions.emptyStateSubtitle}
                />
              ) : (
                <Stack gap="spacing-m">
                  {!!filteredCustomFonts.length && (
                    <FontPairsPresetsGroup
                      isLoading={isLoading}
                      fonts={filteredCustomFonts}
                      fontsData={fontsData}
                      activeFontId={activeFontId}
                      setActiveFont={setActiveFont}
                      fontObserver={fontObserver}
                      title={captions.customPairsTitle}
                      {...(onEditFontPairClick && {
                        onEditClick: onEditFontPairClick,
                        editLabel: captions.editLabel,
                      })}
                      {...(onDeleteFontPairClick && {
                        onDeleteClick: onDeleteFontPairClick,
                        deleteLabel: captions.deleteLabel,
                      })}
                    />
                  )}
                  {!!filteredFonts.length && (
                    <FontPairsPresetsGroup
                      isLoading={isLoading}
                      fonts={filteredFonts}
                      fontsData={fontsData}
                      activeFontId={activeFontId}
                      setActiveFont={setActiveFont}
                      fontObserver={fontObserver}
                      {...(filteredCustomFonts.length && { title: captions.libraryTitle })}
                      {...(onEditFontPairClick && {
                        onEditClick: onEditFontPairClick,
                        editLabel: captions.editLabel,
                      })}
                    />
                  )}
                </Stack>
              )}
            </ContentBox>
            {onAddFontPairClick && (
              <div className="ws-design-fonts-dialog__floating-action-wrapper">
                <FloatingAction onClick={onAddFontPairClick} />
              </div>
            )}
          </div>
        </Panel>
        <Panel corners="squared" style={{ flexShrink: '0' }}>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

DesignFontsDialog.propTypes = {
  isLoading: PropTypes.bool,
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  fonts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      previewUrl: PropTypes.string.isRequired,
    })
  ).isRequired,
  fontsData: PropTypes.arrayOf(
    PropTypes.shape({
      family: PropTypes.string,
      variants: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  customFonts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  activeFontId: PropTypes.string.isRequired,
  setActiveFont: PropTypes.func.isRequired,
  onAddFontPairClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onDeleteFontPairClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onEditFontPairClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  fontObserver: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
  captions: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    emptyStateTitle: PropTypes.string,
    emptyStateSubtitle: PropTypes.string,
    customPairsTitle: PropTypes.string,
    libraryTitle: PropTypes.string,
    deleteLabel: PropTypes.string,
    editLabel: PropTypes.string,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
};

export default DesignFontsDialog;
